import { isLockedDeposit } from './isLockedDeposit'
import { BottomSheetModal, BottomSheetView } from '@gorhom/bottom-sheet'
import { useStatsigClient } from '@statsig/react-bindings'
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Share } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { SelectedChainRow } from '../components/SelectedChainRow'
import { useCheckoutBlocked } from '../geo/useCheckoutBlocked'
import { useFiatRegionSupport } from '../geo/useFiatRegionSupport'
import { useNestedSheet } from '../hooks/useNestedSheet'
import { useFunkitIdentity } from '../identity/IdentityContext'
import { CryptoBitcoinIcon } from '../icons/CryptoBitcoinIcon'
import { PaymentMethodIcon } from '../icons/PaymentMethodIcon'
import { QrChainCluster } from '../icons/QrChainCluster'
import { SourceTokenCluster } from '../icons/SourceTokenCluster'
import { TokenChainIcon } from '../icons/TokenChainIcon'
import { logger } from '../logging/logger'
import { useFunkitTranslation } from '../providers/FunkitI18nProvider'
import { CheckoutBlockedScreen } from '../screens/CheckoutBlockedScreen'
import { QrCodeScreen } from '../screens/QrCodeScreen'
import {
  ChangeChainScreen,
  SelectChainScreen,
} from '../screens/SelectChainScreen'
import {
  type PaymentMethodOption,
  type ReceiveTokenOption,
  SelectMethodScreen,
} from '../screens/SelectMethodScreen'
import {
  type DepositAsset,
  type DepositSourceToken,
  assetChains,
  depositAddressesEqual,
} from '../transfer/depositAssets'
import { useDepositAssets } from '../transfer/hooks/useDepositAssets'
import { DepositAssetPicker } from './DepositAssetPicker'
import { useLockedTokenAmount } from '../transfer/hooks/useLockedTokenAmount'
import { formatFiatWhole } from '../utils/formatAmount'
import { LockedTransferCryptoScreen } from '../screens/LockedTransferCryptoScreen'
import { TransferCryptoScreen } from '../screens/TransferCryptoScreen'
import { useDynamicConfig } from '../statsig/useDynamicConfig'
import { SwappedScreen } from '../swapped/SwappedScreen'
import {
  type AvailableChain,
  buildAvailableChains,
} from '../transfer/availableChains'
import { toFopRows } from '../transfer/fops'
import { formatDepositAddress } from '../transfer/formatDepositAddress'
import { useCopyDepositAddress } from '../transfer/hooks/useCopyDepositAddress'
import { useNativeCapabilities } from '../providers/NativeCapabilitiesContext'
import { useFiatExchangeRates } from '../transfer/hooks/useFiatExchangeRates'
import { type UseFopsResult, useFops } from '../transfer/hooks/useFops'
import { useSupportedTokens } from '../transfer/hooks/useSupportedTokens'
import { useTokenTransfer } from '../transfer/hooks/useTokenTransfer'
import { QrCode } from '../transfer/QrCode'
import { DepositAmountStep } from './DepositAmountStep'
import { FiatDepositAmountStep } from './FiatDepositAmountStep'
import { FiatRegionBlockedStep } from './FiatRegionBlockedStep'
import { FiatRegionPendingStep } from './FiatRegionPendingStep'
import type {
  DepositCheckoutConfig,
  DepositClientMetadata,
  DepositFlowCallbacks,
  DepositFlowConfig,
  DynamicTargetAssetCandidate,
} from './depositFlowConfig'
import { getDefaultDynamicTargetAsset } from './depositFlowConfig'
import { omitsCryptoDeposit, resolveFiatRail } from './depositMethods'
import type { DepositReceipt } from './useDepositReceivedToast'
import { FlowSheetBackground } from './FlowSheet'
import { sheetSizing, useInertScrollableFocus } from './sheetSizing'
import type { FlowNav, FlowScreen } from './FunkitFlowProvider'
import {
  readSelectedChainId,
  writeSelectedChainId,
} from './selectedChainStorage'

/**
 * Screens presented as a nested sheet on top of the current FlowScreen. Adding
 * one is a member here plus a branch in {@link DepositNestedSheet}.
 */
type NestedScreen = 'selectAsset' | 'selectChain' | 'changeChain' | 'qrCode'

/** Fill the sheet instead of hugging content; a record so new screens must choose. */
const FULL_HEIGHT_SCREENS: Record<NestedScreen, boolean> = {
  selectAsset: true,
  selectChain: true,
  changeChain: true,
  qrCode: false,
}

/**
 * Nested screens the step underneath stays presented for (gorhom `push`) rather
 * than minimizing behind (`switch`).
 * - Add money hugs its content while the locked transfer fills the window, so
 *   confirming an asset re-sizes the step under this sheet.
 * - gorhom restores a minimized step to where it left, which can strand the
 *   transfer at Add money's height; a presented step re-snaps in place.
 */
const KEEPS_STEP_PRESENTED: Record<NestedScreen, boolean> = {
  selectAsset: true,
  selectChain: false,
  changeChain: false,
  qrCode: false,
}

// The synthetic id of the built-in "Crypto" funding row. Payment methods are
// derived internally (not host-configured), mirroring web — the crypto row
// routes into the Transfer screen; the rest are Swapped FOPs.
const CRYPTO_METHOD_ID = 'crypto'

/**
 * Every gate that changes what the Add money list shows, so no row pops in late.
 * - `isStatsigLoading` counts: `isSwappedEnabled` reads `false` until it resolves,
 *   indistinguishable from an off gate.
 * - No methods after loading is an error, not an empty sheet; `canRetry` goes
 *   false when a refetch would return the same empty list.
 */
function addMoneyGates({
  isLoadingGeoCheck,
  isAddressLoading,
  isStatsigLoading,
  isChainListLoading,
  fops,
  methodCount,
}: {
  isLoadingGeoCheck: boolean
  isAddressLoading: boolean
  isStatsigLoading: boolean
  isChainListLoading: boolean
  fops: UseFopsResult
  methodCount: number
}) {
  const isLoading =
    isLoadingGeoCheck ||
    isAddressLoading ||
    isStatsigLoading ||
    isChainListLoading ||
    (fops.isSwappedEnabled && fops.isLoading)
  const isFetchError = fops.isSwappedEnabled && fops.isError
  const isEmpty = !isLoading && methodCount === 0
  return {
    isLoading,
    isError: isFetchError || isEmpty,
    canRetry: fops.canRefetch,
  }
}

/**
 * The live flow body. Drives the deposit-address + QR generation
 * ({@link useTokenTransfer}) for the open flow and renders the active screen
 * with the resolved data. Kept separate from the provider so its
 * hooks run only while the modal is open (it isn't mounted when closed).
 */
export function FlowContent({
  checkoutId,
  clientMetadata,
  config,
  current,
  nav,
  onCopySuccess,
  showDepositToast,
  callbacks,
}: {
  checkoutId: string
  clientMetadata: DepositClientMetadata | undefined
  config: DepositFlowConfig
  current: FlowScreen | undefined
  nav: FlowNav
  onCopySuccess: (label?: string) => void
  /** Raises the SDK's deposit confirmation, which outlives this content. */
  showDepositToast: (receipt: DepositReceipt) => void
  callbacks: DepositFlowCallbacks
}): ReactNode {
  const locked = isLockedDeposit(config)
  const { copyToClipboard } = useNativeCapabilities()
  const { t } = useFunkitTranslation()
  const insets = useSafeAreaInsets()
  const { userId, hideCryptoDeposit: hideCryptoDepositByIdentity } =
    useFunkitIdentity()
  const hideCryptoDeposit =
    hideCryptoDepositByIdentity || omitsCryptoDeposit(config.methods)
  const { isGeoblocked, isLoadingGeoCheck } = useCheckoutBlocked({
    onCheckoutBlocked: callbacks.onCheckoutBlocked,
  })
  // Only the fiat rail is gated on this. A region we cannot price still takes
  // a crypto deposit, so the sheet is not what gets refused.
  const fiatRegion = useFiatRegionSupport()

  const { selectedChainId, rememberChainId } = useRememberedChain(userId)
  const targetAsset = useDepositTargetAsset(config)
  const {
    depositAddress: generatedAddress,
    qrCodeUri,
    transferInit,
    isLoading: isAddressLoading,
  } = useTokenTransfer({
    checkoutConfig: targetAsset.activeCheckoutConfig,
    selectedChainId: selectedChainId ? Number(selectedChainId) : undefined,
    dynamicRoutingId: targetAsset.activeDynamicRoutingId,
    checkoutId,
    clientMetadata,
    enabled: true,
  })

  // Curated deposit chains, fetched + resolved from the Statsig
  // `tokentransferconfig` (host config can override). `transferInit` gates
  // Solana/Bitcoin/Tron until their deposit address exists. The chain list
  // drives the "Available chains" screen.
  const fetched = useSupportedTokens({ transferInit })
  const tokenChainFilters = config.tokenChainFilters ?? fetched.chainFilters
  // Per-chain minimum transfer values (Statsig), mirroring web's TransferToken.
  const minTransferLimits = useDynamicConfig('mintokentransfervalue')

  // Swapped forms of payment (Card / Apple Pay / Revolut Pay / Bank Transfer),
  // fetched the same way web does (minus geo). Mobile has no Crypto/Cash tabs —
  // these are merged into the single Add money list (saved FOPs on top, then
  // Crypto, then the rest), so `isSwappedEnabled` just decides whether the cash
  // methods are present.
  const fops = useFops({
    checkoutConfig: targetAsset.activeCheckoutConfig,
    dynamicRoutingId: targetAsset.activeDynamicRoutingId,
    checkoutId,
    clientMetadata,
    initialAmount: config.initialAmount,
    lockAmount: locked,
  })

  // Statsig readiness gates the Add money skeleton: until it resolves,
  // `isSwappedEnabled` reads its `false` fallback (see `isAddMoneyLoading`).
  const { isLoading: isStatsigLoading } = useStatsigClient()

  // The embedded-flow URL of the cash method the user picked (drives the
  // Swapped WebView screen).
  const [selectedSwappedUrl, setSelectedSwappedUrl] = useState<string>()

  const minDepositTarget = useMemo(
    () => ({
      targetChain: targetAsset.activeCheckoutConfig?.targetChain,
      targetAsset: targetAsset.activeCheckoutConfig?.targetAsset,
      dynamicRoutingId: targetAsset.activeDynamicRoutingId,
      getMinDepositUSD: config.checkoutConfig?.getMinDepositUSD,
    }),
    [
      targetAsset.activeCheckoutConfig?.targetChain,
      targetAsset.activeCheckoutConfig?.targetAsset,
      targetAsset.activeDynamicRoutingId,
      config.checkoutConfig?.getMinDepositUSD,
    ],
  )

  // The pickers list EVM and non-EVM chains together, so no network filter.
  const availableChains = useMemo(
    () =>
      buildAvailableChains({
        t,
        chainFilters: tokenChainFilters,
        minTransferLimits,
        target: minDepositTarget,
      }),
    [t, tokenChainFilters, minTransferLimits, minDepositTarget],
  )

  const {
    assets,
    allChains,
    selectedChain,
    activeSourceToken,
    setSelectedSourceToken,
    isChainListLoading,
    isAssetListLoading,
  } = useDepositSource({
    config,
    fetched,
    availableChains,
    transferInit,
    selectedChainId,
  })
  const tokenEstimate = useLockedTokenAmount(
    config.initialAmount,
    activeSourceToken,
    locked && current === 'transfer',
  )

  // Transfer can't render without a chain, so head back to Add money — the
  // Crypto row reads the same value and reopens the picker.
  useEffect(() => {
    if (
      current === 'transfer' &&
      ((allChains.length > 0 && !selectedChain) ||
        (locked && !activeSourceToken && !isAssetListLoading))
    ) {
      nav.back()
    }
  }, [
    current,
    allChains.length,
    selectedChain,
    nav,
    locked,
    activeSourceToken,
    isAssetListLoading,
  ])

  const selectChain = (chain: AvailableChain) => {
    // Source-chain decision point: drives the deposit address + QR generation.
    logger.info('connect-rn:deposit:chain_selected', {
      checkoutId,
      chainId: chain.id,
      chainName: chain.name,
    })
    rememberChainId(chain.id)
  }

  // Prefer the generated address; fall back to a host-supplied one while it
  // loads or when no identity is configured.
  const rawAddress = generatedAddress ?? config.depositAddress
  const displayAddress = rawAddress
    ? formatDepositAddress(rawAddress)
    : t('transferCrypto.generating')

  const copyAddressAndToast = useCopyDepositAddress({
    copyToClipboard,
    onCopySuccess,
    // Locked deposits offer two copyable values; wait for an explicit choice.
    autoCopyAddress: current === 'transfer' && !locked,
    address: rawAddress,
  })

  const {
    nestedSheetRef,
    nestedScreen,
    setNestedScreen,
    openNestedScreen,
    closeNestedScreen,
    renderNestedBackdrop,
  } = useNestedSheet<NestedScreen>()
  const isFullHeightScreen = !!nestedScreen && FULL_HEIGHT_SCREENS[nestedScreen]

  const selectAsset = (token: DepositSourceToken, chain: AvailableChain) => {
    logger.info('connect-rn:deposit:asset_selected', {
      checkoutId,
      symbol: token.symbol,
      chainId: token.chainId,
      address: token.address,
    })
    setSelectedSourceToken(token)
    selectChain(chain)
    closeNestedScreen()
    if (current !== 'transfer') {
      nav.navigate('transfer')
    }
  }

  const openSwappedFlow = (embeddedFlowUrl: string) => {
    setSelectedSwappedUrl(embeddedFlowUrl)
    nav.navigate('swapped')
  }

  const addMoneyMethods = useAddMoneyMethods({
    allChains,
    selectedChain,
    fops,
    targetAsset,
    hideCryptoDeposit,
    cryptoDisabled: isAssetListLoading,
    nav,
    openNestedScreen,
    openSwappedFlow,
    onSelectCrypto: locked
      ? () => {
          if (activeSourceToken) {
            nav.navigate('transfer')
          } else {
            openNestedScreen('selectAsset')
          }
        }
      : undefined,
  })

  const screen = ((): ReactNode => {
    switch (current) {
      // Only reachable when `beginDeposit` opened on it, which requires the
      // config; the guard keeps the types honest.
      case 'depositAmount':
        if (!config.amountEntry) {
          return null
        }
        // With a fiat rail configured the amount screen quotes live and the
        // onramp owns everything after the tap; without one, submission still
        // lands on the host's `onSubmitAmount` seam.
        const fiat = resolveFiatRail(config, targetAsset.activeCheckoutConfig)
        // A provisional verdict always reads as supported, so acting on one
        // would open the flow to a buyer the settled policy denies.
        if (fiat && fiatRegion.isLoading) {
          return <FiatRegionPendingStep />
        }
        if (fiat && !fiatRegion.verdict.supported) {
          return (
            <FiatRegionBlockedStep
              checkoutId={checkoutId}
              callbacks={callbacks}
              onClose={nav.closeFlow}
            />
          )
        }
        return fiat ? (
          <FiatDepositAmountStep
            config={config.amountEntry}
            fiat={fiat}
            checkoutConfig={targetAsset.activeCheckoutConfig}
            dynamicRoutingId={targetAsset.activeDynamicRoutingId}
            depositAddressOverride={config.depositAddress}
            checkoutId={checkoutId}
            clientMetadata={clientMetadata}
            initialAmount={config.initialAmount}
            lockAmount={locked}
            callbacks={callbacks}
            showDepositToast={showDepositToast}
            onCloseFlow={nav.closeFlow}
          />
        ) : (
          <DepositAmountStep
            config={config.amountEntry}
            method={config.methods?.[0]}
            callbacks={callbacks}
            initialAmount={config.initialAmount}
            lockAmount={locked}
          />
        )
      case 'addMoney': {
        const { isLoading, isError, canRetry } = addMoneyGates({
          isLoadingGeoCheck,
          isAddressLoading,
          isStatsigLoading,
          isChainListLoading,
          fops,
          methodCount: addMoneyMethods.methods.length,
        })
        return (
          <SelectMethodScreen
            {...addMoneyMethods}
            title={t('selectMethod.title')}
            errorTitle={t('selectMethod.errorTitle')}
            errorMessage={t('selectMethod.errorMessage')}
            loading={isLoading}
            error={isError}
            onRetry={canRetry ? fops.refetch : undefined}
          />
        )
      }
      case 'transfer':
        if (locked) {
          return (
            <LockedTransferCryptoScreen
              targetAmount={formatFiatWhole(
                Number(config.initialAmount.value),
                config.initialAmount.currency,
              )}
              tokenAmount={tokenEstimate.amount}
              amountError={tokenEstimate.isError}
              amountLoading={tokenEstimate.isLoading}
              onRetryAmount={
                tokenEstimate.canRetry ? tokenEstimate.retry : undefined
              }
              tokenSymbol={activeSourceToken?.symbol ?? ''}
              depositAddress={
                activeSourceToken && selectedChain ? rawAddress : undefined
              }
              selectedChain={selectedChain}
              onCopy={copyAddressAndToast}
              onBack={nav.back}
              onClose={nav.closeFlow}
              onChangeChain={() => openNestedScreen('selectAsset')}
            />
          )
        }
        return selectedChain ? (
          <TransferCryptoScreen
            depositAddress={displayAddress}
            selectedChain={selectedChain}
            onBack={nav.back}
            onChangeChain={() => openNestedScreen('changeChain')}
            onShowQrCode={() => openNestedScreen('qrCode')}
            // Copy/share the FULL address (the screen only shows it truncated).
            // Clipboard is loaded lazily + guarded (see copyToClipboard) so a
            // missing native module degrades gracefully instead of crashing. The
            // toast is shown only after the write lands (see copyAddressAndToast).
            onCopyAddress={
              rawAddress
                ? () => void copyAddressAndToast(rawAddress)
                : undefined
            }
            onShareAddress={
              rawAddress
                ? () => void Share.share({ message: rawAddress })
                : undefined
            }
          />
        ) : null
      case 'swapped':
        return selectedSwappedUrl ? (
          <SwappedScreen
            url={selectedSwappedUrl}
            lockAmount={locked}
            onBack={nav.back}
            onClose={nav.closeFlow}
          />
        ) : null
      default:
        return null
    }
  })()

  if (isGeoblocked) {
    return <CheckoutBlockedScreen />
  }

  return (
    <>
      {screen}
      <BottomSheetModal
        ref={nestedSheetRef}
        stackBehavior={
          nestedScreen && KEEPS_STEP_PRESENTED[nestedScreen] ? 'push' : 'switch'
        }
        {...sheetSizing({ fullHeight: isFullHeightScreen })}
        // Same status-bar cap as FlowSheet — the chain list can grow tall.
        topInset={insets.top}
        // Every nested screen draws its own <Grabber />; `null` suppresses gorhom's.
        handleComponent={null}
        backgroundComponent={FlowSheetBackground}
        backdropComponent={renderNestedBackdrop}
        onDismiss={() => setNestedScreen(null)}
      >
        <BottomSheetView
          focusHook={isFullHeightScreen ? useInertScrollableFocus : undefined}
        >
          <DepositNestedSheet
            nestedScreen={nestedScreen}
            assets={assets}
            allChains={allChains}
            selectedSourceToken={activeSourceToken}
            selectedChain={selectedChain}
            isAssetListLoading={isAssetListLoading}
            isChainListLoading={isChainListLoading}
            selectAsset={selectAsset}
            selectChain={selectChain}
            closeNestedScreen={closeNestedScreen}
            nav={nav}
            qrCode={config.qrCode}
            qrCodeUri={qrCodeUri}
            rawAddress={rawAddress}
            copyAddressAndToast={copyAddressAndToast}
          />
        </BottomSheetView>
      </BottomSheetModal>
    </>
  )
}

/** What {@link SelectMethodScreen} renders, minus its i18n strings + load state. */
interface AddMoneyMethods {
  methods: PaymentMethodOption[]
  receiveTokens: ReceiveTokenOption[] | undefined
  selectedReceiveTokenId: string | undefined
  onSelectReceiveToken: (id: string) => void
  onSelectMethod: (id: string) => void
}

/**
 * The Add money list — saved FOPs, the built-in Crypto row, cash FOPs — and
 * where picking a row routes to. Derivation only: the caller owns the loading
 * and error gates, since those depend on flow-wide state this hook never sees.
 */
function useAddMoneyMethods({
  allChains,
  selectedChain,
  fops,
  targetAsset,
  hideCryptoDeposit,
  cryptoDisabled,
  nav,
  openNestedScreen,
  openSwappedFlow,
  onSelectCrypto,
}: {
  allChains: AvailableChain[]
  /** Gates the Crypto row: with no chain yet, pick one before Transfer. */
  selectedChain: AvailableChain | undefined
  fops: UseFopsResult
  targetAsset: DepositTargetAsset
  hideCryptoDeposit: boolean
  cryptoDisabled: boolean
  nav: FlowNav
  openNestedScreen: (screen: NestedScreen) => void
  openSwappedFlow: (embeddedFlowUrl: string) => void
  onSelectCrypto?: () => void
}): AddMoneyMethods {
  const { t } = useFunkitTranslation()
  const { savedFops, genericFops, isSwappedEnabled } = fops
  const { candidates, selectedCandidate, setSelectedCandidate } = targetAsset

  // Only cash rows show a converted limit; skip the fetch when Swapped is off.
  const { data: exchangeRates } = useFiatExchangeRates(isSwappedEnabled)

  // The built-in crypto/transfer row (Figma: Add money crypto row): a
  // Bitcoin glyph leading + a trailing stack of supported source-chain
  // logos with a "+N" overflow. The row is derived internally (not
  // host-supplied, like web); its source icons come from the curated
  // deposit networks (deduped), not a host-passed list.
  const cryptoSourceIcons = allChains.map((c) => c.iconUri)
  const cryptoMethod: PaymentMethodOption[] = hideCryptoDeposit
    ? []
    : [
        {
          id: CRYPTO_METHOD_ID,
          disabled: cryptoDisabled,
          title: t('selectMethod.crypto'),
          subtitle: t('selectMethod.noLimit'),
          icon: <CryptoBitcoinIcon />,
          trailing: cryptoSourceIcons.length ? (
            <SourceTokenCluster icons={cryptoSourceIcons} overflowMode="plus" />
          ) : undefined,
        },
      ]
  const toCashRows = (fops: typeof savedFops): PaymentMethodOption[] =>
    toFopRows(t, fops, exchangeRates).map((row, i) => ({
      id: row.id,
      title: row.name,
      subtitle: row.subtitle,
      icon: <PaymentMethodIcon fop={fops[i]} />,
    }))
  // Single flat list (no tabs on mobile): saved FOPs on top, then the
  // Crypto row, then the generic cash FOPs — matching the Figma order. When
  // Swapped is off there are no FOPs, so it's just the host's methods.
  const methods: PaymentMethodOption[] = isSwappedEnabled
    ? [...toCashRows(savedFops), ...cryptoMethod, ...toCashRows(genericFops)]
    : cryptoMethod
  // Receive-token dropdown options. Keyed by array index — the only handle
  // guaranteed unique + stable across candidates, since `tokenSymbol` can
  // repeat (USDG with Perps/Spot `badgeText`) and `dynamicRoutingId` can be
  // absent. Selecting one rebinds the target asset (see
  // {@link useDepositTargetAsset}).
  const receiveTokens: ReceiveTokenOption[] | undefined = candidates?.map(
    (c, i) => ({
      id: String(i),
      label: c.badgeText ? `${c.tokenSymbol} (${c.badgeText})` : c.tokenSymbol,
      icon: <TokenChainIcon symbol={c.tokenSymbol} iconSrc={c.iconSrc} />,
    }),
  )
  const selectedReceiveTokenId =
    selectedCandidate && candidates
      ? String(candidates.indexOf(selectedCandidate))
      : undefined

  return {
    methods,
    receiveTokens,
    selectedReceiveTokenId,
    onSelectReceiveToken: (id) => {
      const picked = candidates?.[Number(id)]
      if (picked) {
        setSelectedCandidate(picked)
      }
    },
    onSelectMethod: (id) => {
      if (id === CRYPTO_METHOD_ID) {
        if (onSelectCrypto) {
          onSelectCrypto()
          return
        }
        // First deposit picks a chain first, so the address + QR are
        // generated for one the user chose; after that, straight through.
        if (selectedChain == null) {
          openNestedScreen('selectChain')
          return
        }
        nav.navigate('transfer')
        return
      }
      // FOP rows use a `<fopType>:<name>` id (see `toFopRows`). Open the
      // selected method's Swapped embedded flow in the WebView screen.
      if (id.includes(':')) {
        const fop = [...savedFops, ...genericFops].find(
          (f) => `${f.fopType}:${f.name}` === id,
        )
        if (fop?.embeddedFlowUrl) {
          openSwappedFlow(fop.embeddedFlowUrl)
        }
        return
      }
      // TODO(connect-rn): other host methods route to their own flows
      // once those land.
    },
  }
}

/**
 * Content of the overlay sheet on top of the current deposit screen. Every
 * branch overlays rather than pushes, so Add money stays the stack root and
 * Transfer's back button returns there instead of to a picker.
 */
function DepositNestedSheet({
  nestedScreen,
  assets,
  allChains,
  selectedSourceToken,
  selectedChain,
  isAssetListLoading,
  isChainListLoading,
  selectAsset,
  selectChain,
  closeNestedScreen,
  nav,
  qrCode,
  qrCodeUri,
  rawAddress,
  copyAddressAndToast,
}: {
  nestedScreen: NestedScreen | null
  assets: DepositAsset[]
  allChains: AvailableChain[]
  selectedSourceToken: DepositSourceToken | undefined
  selectedChain: AvailableChain | undefined
  isAssetListLoading: boolean
  isChainListLoading: boolean
  selectAsset: (token: DepositSourceToken, chain: AvailableChain) => void
  selectChain: (chain: AvailableChain) => void
  closeNestedScreen: () => void
  nav: FlowNav
  /** Host-supplied QR override; falls back to one rendered from `qrCodeUri`. */
  qrCode: ReactNode
  qrCodeUri: string | undefined
  rawAddress: string | undefined
  copyAddressAndToast: (value: string) => Promise<boolean>
}): ReactNode {
  switch (nestedScreen) {
    case 'selectAsset':
      return (
        <DepositAssetPicker
          assets={assets}
          chains={allChains}
          selectedToken={selectedSourceToken}
          loading={isAssetListLoading}
          onClose={closeNestedScreen}
          onSelect={selectAsset}
        />
      )
    case 'selectChain':
      return (
        <SelectChainScreen
          chains={allChains}
          selectedChain={selectedChain}
          loading={isChainListLoading}
          onBack={closeNestedScreen}
          onSelectChain={(chain) => {
            selectChain(chain)
            closeNestedScreen()
            nav.navigate('transfer')
          }}
        />
      )
    case 'changeChain':
      return (
        <ChangeChainScreen
          chains={allChains}
          selectedChain={selectedChain}
          onCancel={closeNestedScreen}
          onSelectChain={(chain) => {
            selectChain(chain)
            closeNestedScreen()
          }}
        />
      )
    case 'qrCode':
      return (
        <QrCodeScreen
          qrCode={
            qrCode ??
            (qrCodeUri ? <QrCode value={qrCodeUri} size={235} /> : null)
          }
          // Selected network's logos overlaid on the QR center (single network →
          // one logo; multi-chain → a 2×2 grid).
          centerIcon={
            <QrChainCluster
              iconKeys={selectedChain ? [selectedChain.iconUri] : []}
            />
          }
          // The full (untruncated) address; omitted while it's still
          // generating (the QR screen hides the address block until then).
          address={rawAddress}
          // Returns the copy's success so the screen only confirms "Copied!"
          // when the write actually landed (copyToClipboard is warn-only on
          // failure). Clipboard is loaded lazily + guarded (see copyToClipboard)
          // so a missing native module degrades gracefully instead of crashing.
          // The "Address copied" toast is also shown only on a successful write
          // (see copyAddressAndToast).
          onCopyAddress={
            rawAddress ? () => copyAddressAndToast(rawAddress) : undefined
          }
          onClose={closeNestedScreen}
        />
      )
    default:
      return null
  }
}

interface DepositTargetAsset {
  /** The receive-token dropdown's options, in config order. */
  candidates: DynamicTargetAssetCandidate[] | undefined
  selectedCandidate: DynamicTargetAssetCandidate | undefined
  setSelectedCandidate: (candidate: DynamicTargetAssetCandidate) => void
  activeCheckoutConfig: DepositCheckoutConfig | undefined
  activeDynamicRoutingId: string | undefined
}

function useDepositSource({
  config,
  fetched,
  availableChains,
  transferInit,
  selectedChainId,
}: {
  config: DepositFlowConfig
  fetched: ReturnType<typeof useSupportedTokens>
  availableChains: AvailableChain[]
  transferInit: ReturnType<typeof useTokenTransfer>['transferInit']
  selectedChainId: string | undefined
}) {
  const locked = isLockedDeposit(config)
  const [selectedSourceToken, setSelectedSourceToken] =
    useState<DepositSourceToken>()
  // Until the fetch settles there are no chains, so the Crypto row's trailing
  // logo cluster would render empty and pop in after. Only wait when the list
  // is actually being fetched (a host-supplied one is ready immediately).
  const isChainListLoading = !config.tokenChainFilters && fetched.isLoading
  // A locked deposit converts into a source token, so it cannot offer an asset
  // until curated metadata supplies that token's precision.
  const isAssetListLoading = isChainListLoading || (locked && fetched.isLoading)
  const assets = useDepositAssets({
    tokens: fetched.tokens,
    chains: availableChains,
    metadata: fetched.tokens,
    enabled: locked,
    metadataReady: !fetched.isLoading,
    transferInit,
  })
  const allChains = locked
    ? assetChains(assets, availableChains)
    : availableChains
  const selectedChain = allChains.find((chain) => chain.id === selectedChainId)
  const activeSourceToken = assets
    .flatMap((asset) => asset.tokens)
    .find(
      (token) =>
        token.chainId === selectedSourceToken?.chainId &&
        depositAddressesEqual(token.address, selectedSourceToken.address) &&
        String(token.chainId) === selectedChain?.id,
    )

  return {
    assets,
    allChains,
    selectedChain,
    activeSourceToken,
    setSelectedSourceToken,
    isChainListLoading,
    isAssetListLoading,
  }
}

/**
 * The target token a deposit routes to, and the receive-token candidate the
 * user picked. Selecting a candidate re-targets the deposit address + QR and
 * the `dynamicRoutingId` in lockstep.
 */
function useDepositTargetAsset(config: DepositFlowConfig): DepositTargetAsset {
  // Initialised to the resolved default for the config's `dynamicRoutingId`.
  const [selectedCandidate, setSelectedCandidate] = useState(() =>
    getDefaultDynamicTargetAsset(
      config.dynamicTargetAssetCandidates,
      config.dynamicRoutingId,
    ),
  )

  // The candidate's chain/asset win; the routing id falls back to the config's
  // existing value (NOT cleared) when the candidate omits one.
  const activeCheckoutConfig = selectedCandidate
    ? {
        ...config.checkoutConfig,
        targetChain: selectedCandidate.tokenChainId,
        targetAsset: selectedCandidate.tokenAddress,
      }
    : config.checkoutConfig
  const activeDynamicRoutingId =
    selectedCandidate?.dynamicRoutingId ?? config.dynamicRoutingId

  return {
    candidates: config.dynamicTargetAssetCandidates,
    selectedCandidate,
    setSelectedCandidate,
    activeCheckoutConfig,
    activeDynamicRoutingId,
  }
}

interface RememberedChain {
  selectedChainId: string | undefined
  /** Persist the pick for this identity and apply it immediately. */
  rememberChainId: (chainId: string) => void
}

/**
 * The deposit chain the user picked, remembered across launches.
 *
 * The id, not the row — the row is derived from the curated chain list, so a
 * chain disabled after it was picked stops resolving instead of going stale.
 */
function useRememberedChain(userId: string): RememberedChain {
  const [selectedChainId, setSelectedChainId] = useState<string>()

  useEffect(() => {
    let active = true
    // The selection belongs to an identity, so drop the previous one's before
    // reading; `current ??` alone can't tell a stale pick from a fresh one.
    setSelectedChainId(undefined)
    void readSelectedChainId(userId).then((storedId) => {
      // `active` drops a read whose identity has changed; `current ??` keeps a
      // slow one from clobbering a fresh pick.
      if (active && storedId) {
        setSelectedChainId((current) => current ?? storedId)
      }
    })
    return () => {
      active = false
    }
  }, [userId])

  const rememberChainId = useCallback(
    (chainId: string) => {
      setSelectedChainId(chainId)
      void writeSelectedChainId(userId, chainId)
    },
    [userId],
  )

  return { selectedChainId, rememberChainId }
}
